import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

/*
 * Ported from the Lovable build, then made to survive a phone.
 *
 * The original drew into a fixed 520x240 viewBox and let CSS scale it to the
 * container. On a 1440 screen that is roughly 1:1 and looks right. On a 390
 * screen the container is about 342px, so everything inside was multiplied by
 * 0.66 - including the type. The axis labels rendered at a shade over six
 * pixels and the covenant annotation was illegible, which is a poor advert for
 * a firm selling reporting.
 *
 * So the viewBox now tracks the measured width, and the height is set in
 * pixels. One user unit is one CSS pixel at every size, which means the type
 * is simply the size it says it is, and the geometry re-lays out around the
 * space actually available rather than being squashed into a shrunken copy of
 * the desktop drawing.
 */

const data = [
  { month: "Apr", value: 2400 },
  { month: "May", value: 2180 },
  { month: "Jun", value: 1990 },
  { month: "Jul", value: 1760 },
  { month: "Aug", value: 1610 },
  { month: "Sep", value: 1420 },
  { month: "Oct", value: 1240 },
  { month: "Nov", value: 1090 },
];

const Y_MIN = 800;
const Y_MAX = 2600;
const MIN_CASH = 1000;
const FONT = 9.5;

/** Width the chart assumes before it has been measured, and on the server. */
const FALLBACK_W = 520;

const gridlines = [2600, 2000, 1400, 800];
const gridLabel = (v: number) => `${(v / 1000).toFixed(1)}m`;

/**
 * Geometry for a given rendered width.
 *
 * Height grows a little as the chart narrows. A fixed aspect ratio turned a
 * 342px-wide chart into a 158px-tall sliver, which is not enough vertical room
 * to read a downward trend against a covenant line - the whole point of the
 * graphic.
 */
function geometry(w: number) {
  const narrow = w < 480;
  const h = narrow ? 250 : 240;
  const m = { top: 14, right: 12, bottom: 28, left: narrow ? 34 : 40 };

  const plotW = Math.max(1, w - m.left - m.right);
  const plotH = Math.max(1, h - m.top - m.bottom);

  const x = (i: number) => m.left + (plotW * i) / (data.length - 1);
  const y = (v: number) => m.top + plotH * (1 - (v - Y_MIN) / (Y_MAX - Y_MIN));

  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d.value)}`).join(" ");
  const areaPath = `${linePath} L${x(data.length - 1)},${m.top + plotH} L${m.left},${m.top + plotH} Z`;

  /*
   * Eight month labels need about 34px each before "Apr" and "May" start
   * touching. Below that, label every other month - a gap in the axis reads
   * fine, overlapping type does not.
   */
  const step = plotW / (data.length - 1);
  const labelEvery = step < 34 ? 2 : 1;

  return { w, h, m, plotW, plotH, x, y, linePath, areaPath, labelEvery, narrow };
}

const CashRunwayChart = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(FALLBACK_W);
  const [active, setActive] = useState<number | null>(null);

  // Measured before paint, so the chart never shows a frame at the fallback
  // width and then jumps.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const w = el.getBoundingClientRect().width;
    if (w > 0) setWidth(w);
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      // Rounded, so a sub-pixel reflow does not re-render the whole chart.
      if (w > 0) setWidth((prev) => (Math.abs(prev - w) < 1 ? prev : w));
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const g = useMemo(() => geometry(width), [width]);

  const handleMove = (clientX: number) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // One unit is one pixel now, so this is a straight offset rather than a
    // rescale into viewBox space.
    const px = clientX - rect.left;
    let nearest = 0;
    let best = Infinity;
    data.forEach((_, i) => {
      const dist = Math.abs(g.x(i) - px);
      if (dist < best) {
        best = dist;
        nearest = i;
      }
    });
    setActive(nearest);
  };

  const activePoint = active !== null ? data[active] : null;

  return (
    <div ref={wrapRef}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${g.w} ${g.h}`}
        role="img"
        aria-label="Illustrative cash runway chart: forecast cash balance falls from £2.4m in April to £1.09m in November, against a minimum cash covenant of £1.0m."
        style={{ width: "100%", height: g.h, display: "block", touchAction: "pan-y" }}
        onMouseMove={(e) => handleMove(e.clientX)}
        onMouseLeave={() => setActive(null)}
        onTouchStart={(e) => handleMove(e.touches[0].clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={() => setActive(null)}
      >
        <defs>
          <linearGradient id="runway-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--green))" stopOpacity="0.28" />
            <stop offset="100%" stopColor="hsl(var(--green))" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridlines.map((v) => (
          <g key={v}>
            <line
              x1={g.m.left}
              x2={g.w - g.m.right}
              y1={g.y(v)}
              y2={g.y(v)}
              stroke="hsl(var(--rule))"
              strokeWidth="1"
            />
            <text
              x={g.m.left - 8}
              y={g.y(v) + 3}
              textAnchor="end"
              fontFamily="var(--font-mono, monospace)"
              fontSize={FONT}
              fill="hsl(var(--muted-foreground))"
            >
              {gridLabel(v)}
            </text>
          </g>
        ))}

        <path d={g.areaPath} fill="url(#runway-fill)" />
        <path
          d={g.linePath}
          fill="none"
          stroke="hsl(var(--green))"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        <line
          x1={g.m.left}
          x2={g.w - g.m.right}
          y1={g.y(MIN_CASH)}
          y2={g.y(MIN_CASH)}
          stroke="hsl(var(--amber))"
          strokeWidth="1"
          strokeDasharray="4 4"
        />
        {/* Anchored at the left end of the covenant line: the forecast line
            terminates at the right edge just above it, and a right-anchored
            label sat on top of the endpoint dot. The tracking drops on a
            narrow chart, where the full "MIN CASH £1.0M" at 1.4 would have run
            past the plot. */}
        <text
          x={g.m.left + 8}
          y={g.y(MIN_CASH) - 10}
          textAnchor="start"
          fontFamily="var(--font-mono, monospace)"
          fontSize={FONT}
          letterSpacing={g.narrow ? 0.6 : 1.4}
          fill="hsl(var(--amber))"
        >
          MIN CASH £1.0M
        </text>

        {data.map((d, i) =>
          i % g.labelEvery === 0 ? (
            <text
              key={d.month}
              x={g.x(i)}
              y={g.h - 8}
              textAnchor="middle"
              fontFamily="var(--font-mono, monospace)"
              fontSize={FONT}
              fill="hsl(var(--muted-foreground))"
            >
              {d.month}
            </text>
          ) : null,
        )}

        <circle
          cx={g.x(data.length - 1)}
          cy={g.y(data[data.length - 1].value)}
          r="4.5"
          fill="hsl(var(--green))"
          stroke="hsl(var(--card))"
          strokeWidth="2.5"
        />

        {active !== null && activePoint && (
          <g>
            <line
              x1={g.x(active)}
              x2={g.x(active)}
              y1={g.m.top}
              y2={g.m.top + g.plotH}
              stroke="hsl(var(--rule-2))"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <circle
              cx={g.x(active)}
              cy={g.y(activePoint.value)}
              r="4"
              fill="hsl(var(--green))"
              stroke="hsl(var(--card))"
              strokeWidth="2"
            />
            <g
              transform={`translate(${Math.min(
                Math.max(g.x(active), g.m.left + 46),
                g.w - g.m.right - 46,
              )}, ${Math.max(g.y(activePoint.value) - 14, g.m.top + 12)})`}
            >
              <rect x="-46" y="-14" width="92" height="19" rx="1" fill="hsl(var(--foreground))" />
              <text
                x="0"
                y="-1"
                textAnchor="middle"
                fontFamily="var(--font-mono, monospace)"
                fontSize={FONT}
                fill="hsl(var(--card))"
              >
                {`${activePoint.month}  £${(activePoint.value / 1000).toFixed(2)}M`}
              </text>
            </g>
          </g>
        )}
      </svg>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="flex items-center gap-2 font-eyebrow text-muted-foreground">
          <svg width="12" height="6" aria-hidden="true">
            <line x1="0" y1="3" x2="12" y2="3" stroke="hsl(var(--green))" strokeWidth="2" />
          </svg>
          Forecast cash balance
        </span>
        <span className="flex items-center gap-2 font-eyebrow text-muted-foreground">
          <svg width="12" height="6" aria-hidden="true">
            <line
              x1="0"
              y1="3"
              x2="12"
              y2="3"
              stroke="hsl(var(--amber))"
              strokeWidth="2"
              strokeDasharray="3 3"
            />
          </svg>
          Minimum cash covenant
        </span>
      </div>
    </div>
  );
};

export default CashRunwayChart;
